import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'react-feather';
import { apiClient } from '../shared/api/client';

interface CheckupItem {
  key: string;
  kind: 'cover' | 'uncategorized' | 'reimbursable' | 'pot' | 'consent';
  title: string;
  detail: string;
  action: string;
  amount: number | null;
}

// The monthly check-up: a handful of things worth acting on, phrased as
// instructions for the BANK - the app can only read, never move money.
// Items are derived from live data, so a fixed situation removes its own
// item on the next refresh.
const MonthCheckup: React.FC<{ onCount?: (n: number) => void }> = ({ onCount }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState<CheckupItem[] | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const load = () =>
    apiClient.get('/checkup')
      .then(response => {
        const list = ((response as any).items || []) as CheckupItem[];
        setItems(list);
        onCount?.(list.length);
      })
      .catch(err => { console.error('Load checkup error:', err); setItems([]); });

  useEffect(() => { load(); }, []);

  const dismiss = async (item: CheckupItem) => {
    try {
      setBusyKey(item.key);
      setItems(prev => {
        const next = (prev || []).filter(i => i.key !== item.key);
        onCount?.(next.length);
        return next;
      });
      await apiClient.post('/checkup/dismiss', { key: item.key });
    } catch (err) {
      console.error('Dismiss checkup item error:', err);
      load();
    } finally {
      setBusyKey(null);
    }
  };

  if (items === null) return null;

  if (items.length === 0) {
    return (
      <p className="flex items-center text-xs text-gray-400 mb-4 px-1">
        <CheckCircle size={13} className="mr-1.5 text-green-500" />
        Månadskoll: inget att åtgärda
      </p>
    );
  }

  // Every item leads somewhere real: either it is done in the bank, or a
  // button takes you to the place in the app where the work happens.
  const labels = (kind: CheckupItem['kind']) =>
    kind === 'cover' ? { primary: 'Jag har fört över', secondary: 'Inte nu', goto: null }
      : kind === 'uncategorized' ? { primary: 'Kategorisera nu', secondary: 'Senare', goto: '/transaktioner?saknar=kategori' }
      : kind === 'reimbursable' ? { primary: 'Visa utläggen', secondary: 'Inte nu', goto: '/transaktioner?utlagg=oskickade' }
      : kind === 'pot' ? { primary: 'Uppdatera saldot', secondary: 'Inte nu', goto: '/settings/goals' }
      : { primary: 'Förnya nu', secondary: 'Senare', goto: '/settings/bank-integration' };

  return (
    <div className="bg-white rounded-xl p-5 mb-4 shadow-sm">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-900">Månadskoll</h2>
        <span className="text-sm text-gray-500">{items.length} att kolla</span>
      </div>

      {items.map(item => {
        const l = labels(item.kind);
        return (
          <div key={item.key} className="border-t border-gray-100 pt-3 mt-3 first:border-0 first:pt-0 first:mt-0">
            <p className="text-sm font-semibold text-gray-900">{item.title}</p>
            <p className="text-xs text-gray-500 mt-0.5">{item.detail}</p>
            <p className="text-sm text-gray-800 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 mt-2">
              {item.action}
            </p>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => {
                  if (l.goto) { navigate(l.goto); return; }
                  dismiss(item);
                }}
                disabled={busyKey === item.key}
                className="inline-flex items-center text-sm font-semibold text-blue-600 hover:bg-blue-50 rounded-lg px-2.5 py-1.5 disabled:opacity-50"
              >
                {l.primary}
                {l.goto && <ArrowRight size={14} className="ml-1" />}
              </button>
              <button
                onClick={() => dismiss(item)}
                disabled={busyKey === item.key}
                className="text-sm text-gray-500 hover:bg-gray-100 rounded-lg px-2.5 py-1.5 disabled:opacity-50"
              >
                {l.secondary}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MonthCheckup;
